import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  RefreshCw,
  Calendar,
  ChevronDown,
  Search,
  Eye,
  Database,
  AlertTriangle,
  GitBranch,
  Building2,
  FileText,
} from 'lucide-react'
import styles from './CorrelationAudit.module.scss'
import { config } from '../../config'

const MONITOR_API_URL = config.MONITOR_API_URL
const API_URL = config.API_URL

interface CorrelationLog {
  _id: string
  _index: string
  _source: {
    correlation_id: string
    status?: string
    pusher_status?: string
    provider?: string
    dealer_id?: string
    dealer_name?: string
    file_path?: string
    metadata?: {
      date?: string
      vin?: string
      stock?: string
      make?: string
      model?: string
      year?: number
    }
    audit_history?: Array<{
      step: string
      status: string
      timestamp: string
      message?: string
    }>
    '@timestamp'?: string
  }
}

interface StatusAgg {
  status: string
  count: number
}

interface AggData {
  total: number
  by_status: StatusAgg[]
  by_provider: Array<{ provider: string; count: number }>
}

// Status display config with colors
const STATUS_COLORS: Record<string, string> = {
  success: '#22c55e',
  error: '#ef4444',
  processing: '#3b82f6',
  pending: '#f59e0b',
  queued: '#8b5cf6',
  failed: '#ef4444',
}

const getStatusColor = (status: string): string => {
  return STATUS_COLORS[status.toLowerCase()] || '#6b7280'
}

const capitalizeStatus = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const CorrelationAudit: React.FC = () => {
  const navigate = useNavigate()
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [providerFilter, setProviderFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [logs, setLogs] = useState<CorrelationLog[]>([])
  const [aggData, setAggData] = useState<AggData>({
    total: 0,
    by_status: [],
    by_provider: [],
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalHits, setTotalHits] = useState(0)
  const [providerNameMap, setProviderNameMap] = useState<Record<string, string>>({})
  const pageSize = 25

  const getDateForApi = (dateString: string): string => {
    const d = new Date(dateString)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
  }

  // Fetch provider names mapping
  useEffect(() => {
    const fetchProviderNames = async () => {
      try {
        const response = await fetch(`${API_URL}/dealer?all=true`, {
          credentials: 'include',
        })
        if (response.ok) {
          const data = await response.json()
          const nameMap: Record<string, string> = {}
          if (data.objects && Array.isArray(data.objects)) {
            data.objects.forEach((dealer: { provider: number; provider_name: string }) => {
              if (dealer.provider && dealer.provider_name) {
                nameMap[String(dealer.provider)] = dealer.provider_name
              }
            })
          }
          setProviderNameMap(nameMap)
        }
      } catch (err) {
        console.error('Failed to fetch provider names:', err)
      }
    }
    fetchProviderNames()
  }, [])

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const dateForApi = getDateForApi(selectedDate)

      const params = new URLSearchParams({
        date: dateForApi,
        page: page.toString(),
        size: pageSize.toString(),
      })

      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (providerFilter !== 'all') params.append('provider', providerFilter)
      if (searchQuery) params.append('q', searchQuery)

      const response = await fetch(`${MONITOR_API_URL}/correlation-logs?${params}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 404 || errorData.error?.includes('index_not_found')) {
          setLogs([])
          setTotalHits(0)
          setAggData({ total: 0, by_status: [], by_provider: [] })
          setError(`No data found for ${selectedDate}. Try selecting a different date.`)
          return
        }
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const data = await response.json()

      setLogs(Array.isArray(data.hits) ? data.hits : [])
      setTotalHits(typeof data.total === 'number' ? data.total : 0)
      setAggData({
        total: data.aggregations?.total ?? data.total ?? 0,
        by_status: data.aggregations?.by_status ?? [],
        by_provider: data.aggregations?.by_provider ?? [],
      })

      if (!data.hits || data.hits.length === 0) {
        setError(`No logs found for ${selectedDate}. Try selecting a different date or adjusting filters.`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs')
      setLogs([])
      setTotalHits(0)
      setAggData({ total: 0, by_status: [], by_provider: [] })
    } finally {
      setLoading(false)
    }
  }, [selectedDate, statusFilter, providerFilter, searchQuery, page])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const handleViewDetails = (log: CorrelationLog) => {
    const correlationId = log._source.correlation_id || log._id
    navigate(`/correlation_audit/${encodeURIComponent(correlationId)}?date=${getDateForApi(selectedDate)}`)
  }

  const handleClearFilters = () => {
    setStatusFilter('all')
    setProviderFilter('all')
    setSearchQuery('')
    setPage(1)
  }

  const getStatusClass = (status: string | undefined): string => {
    if (!status) return styles.statusUnknown
    const map: Record<string, string> = {
      success: styles.statusSuccess,
      failed: styles.statusFailed,
      pending: styles.statusPending,
    }
    return map[status.toLowerCase()] || styles.statusUnknown
  }

  const formatTimestamp = (timestamp: string | undefined): string => {
    if (!timestamp) return '-'
    try {
      return new Date(timestamp).toLocaleString()
    } catch {
      return timestamp
    }
  }

  const total = aggData.total || 0
  const totalPages = Math.ceil(totalHits / pageSize)

  // Progress bar segments from dynamic aggregation
  const statusSegments = aggData.by_status
    .filter((s) => s.count > 0)
    .map((s) => ({
      key: s.status,
      value: s.count,
      color: getStatusColor(s.status),
      label: capitalizeStatus(s.status),
    }))

  // Status options from aggregation (dynamic)
  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    ...aggData.by_status.map((s) => ({
      value: s.status,
      label: `${capitalizeStatus(s.status)} (${s.count.toLocaleString()})`,
    })),
  ]

  // Provider options from aggregation - show provider names instead of codes
  const providerOptions = [
    { value: 'all', label: 'All Providers' },
    ...aggData.by_provider.map((p) => {
      const providerName = providerNameMap[p.provider] || p.provider
      return { value: p.provider, label: `${providerName} (${p.count})` }
    }),
  ]

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.titleGroup}>
            <div className={styles.logoIcon}>
              <GitBranch size={18} />
            </div>
            <h1 className={styles.title}>Correlation Audit</h1>
          </div>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.refreshBtn} onClick={fetchLogs} disabled={loading}>
            <RefreshCw size={14} className={loading ? styles.spinning : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={14} />
          <span>{error}</span>
        </div>
      )}

      {/* Status Overview */}
      <div className={styles.statusOverview}>
        <div className={styles.totalCount}>
          <span className={styles.totalValue}>{total.toLocaleString()}</span>
          <span className={styles.totalLabel}>Total Records</span>
        </div>

        <div className={styles.progressSection}>
          {total > 0 ? (
            <>
              <div className={styles.progressBar}>
                {statusSegments.map((seg) => (
                  <div
                    key={seg.key}
                    className={styles.progressSegment}
                    style={{
                      width: `${(seg.value / total) * 100}%`,
                      backgroundColor: seg.color,
                    }}
                    title={`${seg.label}: ${seg.value.toLocaleString()} (${((seg.value / total) * 100).toFixed(1)}%)`}
                  />
                ))}
              </div>
              <div className={styles.progressLegend}>
                {statusSegments.map((seg) => (
                  <div key={seg.key} className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ backgroundColor: seg.color }} />
                    <span className={styles.legendLabel}>{seg.label}</span>
                    <span className={styles.legendValue}>{seg.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className={styles.noData}>No data available</div>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className={styles.filtersCard}>
        <div className={styles.primaryFilters}>
          {/* Date */}
          <div className={styles.filterItem}>
            <Calendar size={14} className={styles.filterIcon} />
            <input
              type="date"
              className={styles.dateInput}
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value)
                setPage(1)
              }}
            />
          </div>

          {/* Status */}
          <div className={styles.filterItem}>
            <select
              className={styles.filterSelect}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setPage(1)
              }}>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className={styles.selectChevron} />
          </div>

          {/* Provider */}
          <div className={styles.filterItem}>
            <Building2 size={14} className={styles.filterIcon} />
            <select
              className={styles.filterSelect}
              value={providerFilter}
              onChange={(e) => {
                setProviderFilter(e.target.value)
                setPage(1)
              }}
              style={{ paddingLeft: '32px' }}>
              {providerOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className={styles.selectChevron} />
          </div>

          {/* Search */}
          <div className={styles.searchItem}>
            <Search size={14} className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by correlation ID, VIN, stock..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1)
                  fetchLogs()
                }
              }}
            />
          </div>

          <button
            className={styles.applyBtn}
            onClick={() => {
              setPage(1)
              fetchLogs()
            }}>
            Apply
          </button>
          <button className={styles.clearBtn} onClick={handleClearFilters}>
            Clear
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>
            <FileText size={14} />
            <span>Correlation Audit Logs</span>
          </div>
          <div className={styles.tableActions}>
            <span className={styles.tableCount}>{totalHits.toLocaleString()} results</span>
          </div>
        </div>

        {loading ? (
          <div className={styles.loading}>
            <RefreshCw size={24} className={styles.spinning} />
            <span>Loading logs...</span>
          </div>
        ) : logs.length === 0 ? (
          <div className={styles.emptyState}>
            <Database size={48} />
            <h3>No logs found</h3>
            <p>Try adjusting your filters or selecting a different date.</p>
          </div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Correlation ID</th>
                    <th>Status</th>
                    <th>Pusher Status</th>
                    <th>Provider</th>
                    <th>Dealer</th>
                    <th>VIN</th>
                    <th>Make/Model</th>
                    <th>Timestamp</th>
                    <th className={styles.actionsHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td>
                        <code className={styles.codeCell} title={log._source.correlation_id}>
                          {log._source.correlation_id?.substring(0, 30) || log._id.substring(0, 30)}
                          {(log._source.correlation_id?.length || log._id.length) > 30 ? '...' : ''}
                        </code>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${getStatusClass(log._source.status)}`}>
                          {log._source.status || 'unknown'}
                        </span>
                      </td>
                      <td>
                        {log._source.pusher_status ? (
                          <span className={`${styles.statusBadge} ${getStatusClass(log._source.pusher_status)}`}>
                            {log._source.pusher_status}
                          </span>
                        ) : (
                          <span className={styles.muted}>-</span>
                        )}
                      </td>
                      <td>
                        <span className={styles.cellText}>{log._source.provider || '-'}</span>
                      </td>
                      <td>
                        <span className={styles.cellText} title={log._source.dealer_name}>
                          {log._source.dealer_name || log._source.dealer_id || '-'}
                        </span>
                      </td>
                      <td>
                        <code className={styles.codeCell}>{log._source.metadata?.vin || '-'}</code>
                      </td>
                      <td>
                        <span className={styles.cellText}>
                          {log._source.metadata?.make && log._source.metadata?.model
                            ? `${log._source.metadata.make} ${log._source.metadata.model}`
                            : '-'}
                        </span>
                      </td>
                      <td>
                        <span className={styles.cellText}>{formatTimestamp(log._source['@timestamp'])}</span>
                      </td>
                      <td>
                        <div className={styles.rowActions}>
                          <button
                            className={`${styles.iconBtn} ${styles.primary}`}
                            onClick={() => handleViewDetails(log)}
                            title="View Details">
                            <Eye size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <span className={styles.pageInfo}>
                  {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalHits)} of {totalHits.toLocaleString()}
                </span>
                <div className={styles.pageButtons}>
                  <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(1)}>
                    First
                  </button>
                  <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(page - 1)}>
                    Prev
                  </button>
                  <span className={styles.pageNumber}>Page {page}</span>
                  <button className={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                    Next
                  </button>
                  <button className={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(totalPages)}>
                    Last
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default CorrelationAudit
